/**
 * The Bounded Agency Delegation Model
 *
 * A leadership protocol for assigning authority, judgment, action, feedback,
 * and ownership to humans and AI. Evolves the classic "levels of delegation"
 * authority ladder into a transfer of *bounded agency*: every delegation names
 * the agency granted, the boundary it stops at, the feedback required, and the
 * risk / reversibility that should move autonomy up or down.
 *
 * This module is the single source of truth for the model's content. The page
 * at /bounded-agency-delegation renders from these structures.
 */

export type Delegate = 'human' | 'ai';

/** One of the seven bounded-agency levels. */
export interface DelegationLevel {
  level: number;
  /** Short name, e.g. "Act Within Boundaries". */
  name: string;
  /** One-line summary of the instruction pattern. */
  instructionPattern: string;
  /** The agency being transferred at this level. */
  agency: string;
  /** Where delegated authority stops. */
  boundary: string;
  /** What must come back to the leader, and when. */
  feedback: string;
  /** The delegate's role at this level. */
  role: string;
  /** When this level is the right choice. */
  useWhen: string[];
  /** What triggers a stop-and-come-back. */
  escalation: string;
  /** A human-delegation example. */
  humanExample: string;
  /** An AI-delegation example. */
  aiExample: string;
  /** Quick-reference flags. */
  ref: {
    mayDecide: string;
    mayAct: string;
    approvalBeforeAction: string;
    reportingRequired: string;
  };
  /** Tailwind accent token family used for this level's chrome. */
  accent: 'red' | 'orange' | 'amber' | 'sky' | 'indigo' | 'emerald' | 'teal';
}

export const DELEGATION_LEVELS: DelegationLevel[] = [
  {
    level: 1,
    name: 'Execute Precisely',
    instructionPattern:
      'Do exactly this. Do not reinterpret the task. Escalate only if it cannot be followed safely or literally.',
    agency: 'None — the decision has already been made.',
    boundary: 'The exact instruction. No deviation.',
    feedback: 'Confirm completion, especially when material or time-sensitive.',
    role: 'Executor',
    useWhen: [
      'The decision is already made and the correct action is known',
      'Consistency matters more than interpretation',
      'There is little room for judgment',
      'Deviation would create risk',
    ],
    escalation:
      'Escalate if the instruction is unclear, impossible, unsafe, unethical, or likely to cause unintended consequences.',
    humanExample: 'Send the attached agenda to the leadership team exactly as written.',
    aiExample: 'Convert this text into a table. Do not rewrite, summarize, or change the wording.',
    ref: {
      mayDecide: 'No',
      mayAct: 'Yes — exactly as instructed',
      approvalBeforeAction: 'No, if instructions are clear',
      reportingRequired: 'Usually yes',
    },
    accent: 'red',
  },
  {
    level: 2,
    name: 'Investigate and Report',
    instructionPattern:
      'Research this and report back with relevant findings, uncertainties, and gaps. Do not recommend unless asked.',
    agency: 'Gather information.',
    boundary: 'No recommendation or action unless requested.',
    feedback: 'Return findings in a useful structure, with sources, uncertainties, and gaps.',
    role: 'Researcher',
    useWhen: [
      'The leader needs context before deciding',
      'The situation is not yet understood',
      'Facts should be separated from judgment',
      'Decision quality depends on gathering context first',
    ],
    escalation:
      'Escalate if research reveals a material risk, a major assumption is wrong, or the original question appears misframed.',
    humanExample: 'Research three vendors that provide this service and summarize what each offers.',
    aiExample: 'Review these documents and identify the recurring themes. Do not recommend a course of action yet.',
    ref: {
      mayDecide: 'No',
      mayAct: 'No',
      approvalBeforeAction: 'Yes',
      reportingRequired: 'Yes',
    },
    accent: 'orange',
  },
  {
    level: 3,
    name: 'Analyze and Recommend',
    instructionPattern:
      'Analyze the options, recommend one, explain the tradeoffs, and wait for approval before acting.',
    agency: 'Analyze and advise.',
    boundary: 'No action without approval.',
    feedback:
      'Provide a clear recommendation — the chosen option, reasoning, alternatives, pros/cons, risks, open questions, and the decision required.',
    role: 'Advisor',
    useWhen: [
      'The leader wants the delegate’s judgment',
      'The decision requires analysis with multiple plausible options',
      'The leader still owns the final decision',
      'Consequences are meaningful enough to require approval',
    ],
    escalation: 'Escalate if available information is insufficient to support a responsible recommendation.',
    humanExample:
      'Compare the three pricing options and recommend which one we should choose. Do not contact the vendor until I approve.',
    aiExample:
      'Evaluate these positioning statements, explain strengths and weaknesses, and recommend the strongest. Do not finalize the copy yet.',
    ref: {
      mayDecide: 'Recommend only',
      mayAct: 'No',
      approvalBeforeAction: 'Yes',
      reportingRequired: 'Yes',
    },
    accent: 'amber',
  },
  {
    level: 4,
    name: 'Draft or Prepare for Review',
    instructionPattern:
      'Create the work product, but do not publish, send, implement, or commit it until it has been reviewed.',
    agency: 'Produce a work product.',
    boundary: 'No release, publication, sending, spending, or external commitment.',
    feedback:
      'Return the draft with context: what was created, what assumptions were made, what choices need review, and what needs special attention.',
    role: 'Producer',
    useWhen: [
      'The delegate can produce the work but the leader wants review authority before release',
      'The output may have reputational, relational, legal, financial, or strategic consequences',
      'The work is likely to benefit from iteration',
    ],
    escalation:
      'Escalate before sending, publishing, committing funds, changing a system of record, or representing the leader externally.',
    humanExample: 'Draft a response to the client. I want to review it before it goes out.',
    aiExample: 'Create a first draft of the proposal. Do not send it, upload it, or treat it as final.',
    ref: {
      mayDecide: 'Limited production choices',
      mayAct: 'Create only, not release',
      approvalBeforeAction: 'Yes, before release',
      reportingRequired: 'Yes',
    },
    accent: 'sky',
  },
  {
    level: 5,
    name: 'Act Within Boundaries',
    instructionPattern:
      'Proceed within these boundaries. Escalate if cost, risk, uncertainty, reversibility, or stakeholder impact crosses the threshold.',
    agency: 'Decide and act inside an operating envelope.',
    boundary: 'Explicit constraints and escalation triggers — the operating envelope.',
    feedback: 'Report exceptions, meaningful developments, and completion as agreed.',
    role: 'Bounded operator',
    useWhen: [
      'The delegate has enough context to act and the work requires judgment',
      'The leader does not need to approve every step',
      'There are clear limits to the delegate’s authority',
    ],
    escalation:
      'Escalate on cost above threshold, irreversible action, external communication, legal/compliance exposure, reputational risk, interpersonal sensitivity, low confidence, material ambiguity, or a meaningful change in context.',
    humanExample:
      'Handle the vendor issue however you think best, as long as cost stays under $1,000 and we do not change contract terms. Escalate anything beyond that.',
    aiExample:
      'Clean up and organize these notes. You may rename headings and group items, but do not delete anything, invent content, or make commitments on my behalf.',
    ref: {
      mayDecide: 'Yes, within limits',
      mayAct: 'Yes, within limits',
      approvalBeforeAction: 'Only outside boundaries',
      reportingRequired: 'Exception-based',
    },
    accent: 'indigo',
  },
  {
    level: 6,
    name: 'Decide and Inform',
    instructionPattern: 'Make the decision, act, and tell me what you did so I am not surprised.',
    agency: 'Decide and act.',
    boundary: 'Domain scope.',
    feedback:
      'Inform after the fact: what was decided, why, what action was taken, the likely implications, and any follow-up needed.',
    role: 'Delegated decision-maker',
    useWhen: [
      'The delegate has demonstrated good judgment in their domain',
      'Speed matters',
      'The leader does not need to approve in advance, but awareness still matters',
    ],
    escalation:
      'Escalate if the issue moves outside the delegate’s domain, creates unexpected risk, or conflicts with prior direction.',
    humanExample: 'Choose the best facilitator for the workshop and book them. Let me know whom you selected and why.',
    aiExample:
      'Apply the agreed formatting rules to the document, make the necessary corrections, and summarize the changes you made.',
    ref: {
      mayDecide: 'Yes',
      mayAct: 'Yes',
      approvalBeforeAction: 'No',
      reportingRequired: 'Yes, after action',
    },
    accent: 'emerald',
  },
  {
    level: 7,
    name: 'Own the Outcome',
    instructionPattern:
      'Own the outcome. Make the necessary decisions. Escalate only when the outcome, context, or risk materially changes.',
    agency: 'Full outcome ownership.',
    boundary: 'Strategic intent, values, and material escalation.',
    feedback: 'No routine reporting unless agreed, but material developments are still surfaced.',
    role: 'Owner',
    useWhen: [
      'The leader trusts the delegate deeply and the domain is well within their competence',
      'The leader does not need routine visibility',
      'The cost of independent action is acceptable',
    ],
    escalation:
      'Escalate when the outcome is at risk, the context materially changes, or a decision would affect stakeholders beyond the delegated scope.',
    humanExample:
      'You own the onboarding experience. Make whatever improvements you believe are needed. Keep me informed only when something materially changes.',
    aiExample:
      'Operate independently within this defined scope. Keep an audit trail. Escalate when confidence is low, when information is missing, or when an action would become external, irreversible, costly, or reputationally meaningful.',
    ref: {
      mayDecide: 'Yes',
      mayAct: 'Yes',
      approvalBeforeAction: 'No',
      reportingRequired: 'Only when material',
    },
    accent: 'teal',
  },
];

/** The five core dimensions that every delegation should make explicit. */
export interface DelegationDimension {
  name: string;
  question: string;
  detail: string;
}

export const DELEGATION_DIMENSIONS: DelegationDimension[] = [
  {
    name: 'Agency',
    question: 'What may the delegate initiate, interpret, decide, change, or own?',
    detail:
      'The central thing being transferred. At low levels agency stays with the leader; at high levels it moves to the delegate, who owns an outcome inside a defined boundary.',
  },
  {
    name: 'Boundary',
    question: 'Where does delegated authority stop?',
    detail:
      'Trust without boundaries becomes ambiguity. Boundaries can be budget, time, decision, communication, privacy, reversibility, stakeholder, or quality limits, plus escalation conditions.',
  },
  {
    name: 'Feedback',
    question: 'What must come back to the leader, and when?',
    detail:
      'Separates report-before-acting, recommend-before-acting, draft-before-release, act-and-inform, exception-only, and material-change-only modes that otherwise get confused.',
  },
  {
    name: 'Risk & Reversibility',
    question: 'What is the cost of being wrong, and can it be undone?',
    detail:
      'Autonomy can rise as reversibility increases and risk decreases; it should fall as irreversibility, ambiguity, stakeholder impact, or reputational risk rises.',
  },
  {
    name: 'Maturity of the System',
    question: 'How clear, stable, and well-governed is the surrounding context?',
    detail:
      'Depends on the maturity of the person, the process, the context, the relationship, and the risk controls — not the delegate’s competence alone.',
  },
];

/** Common delegation failures and their corrections. */
export interface DelegationFailure {
  title: string;
  problem: string;
  correction: string;
}

export const DELEGATION_FAILURES: DelegationFailure[] = [
  {
    title: 'Assigning a task without delegating authority',
    problem: '"Take care of this," without clarifying whether the person may decide, spend, communicate, or commit.',
    correction: 'Specify the delegation level and boundaries.',
  },
  {
    title: 'Asking for research but expecting judgment',
    problem: 'The leader asks for information, then is frustrated that no recommendation came back.',
    correction: 'Use Level 3 (Analyze and Recommend) instead of Level 2.',
  },
  {
    title: 'Asking for a recommendation but punishing disagreement',
    problem: 'The leader says they want judgment but only rewards agreement.',
    correction: 'Clarify whether the assignment is truly Level 3 or merely Level 2 with analysis.',
  },
  {
    title: 'Giving autonomy without boundaries',
    problem: 'The leader delegates broadly but never specifies risks, constraints, or escalation triggers.',
    correction: 'Use Level 5 (Act Within Boundaries) with an explicit operating envelope.',
  },
  {
    title: 'Treating drafting as permission to publish',
    problem: 'The delegate creates a message or plan and assumes it may be sent or implemented.',
    correction: 'Use Level 4 (Draft or Prepare for Review).',
  },
  {
    title: 'Delegating to AI without an operating envelope',
    problem: 'Asking AI to "handle it" without saying what it may change, decide, send, delete, or assume.',
    correction: 'State permissions, constraints, sources, confidence requirements, and escalation triggers explicitly.',
  },
];

/**
 * AI delegation guardrails — the explicit constraints a leader should set when
 * the delegate is an AI rather than a human.
 */
export const AI_GUARDRAILS: string[] = [
  'Whether the AI may act or only advise',
  'Whether the AI may use external sources, and whether sources must be cited',
  'Whether uncertainty must be surfaced',
  'Whether the AI may edit, delete, or transform original material',
  'Whether the AI may communicate externally',
  'Whether the AI may make commitments',
  'Whether the action is reversible',
  'Whether the task involves confidential information',
  'What should trigger escalation back to the human',
];

export const AI_SAFE_DEFAULT =
  'AI may proceed independently on private, reversible, low-risk work within clear constraints. AI should escalate before external, irreversible, costly, confidential, relationally sensitive, legal, financial, or reputationally significant action.';

/**
 * The Agency × Risk/Reversibility matrix. Given how reversible and how risky an
 * action is, this is the highest autonomy level that is usually appropriate.
 * Rule: autonomy rises with reversibility, falls with risk and irreversibility.
 */
export type Reversibility = 'reversible' | 'irreversible';
export type RiskBand = 'low' | 'moderate' | 'high';

export interface MatrixCell {
  reversibility: Reversibility;
  risk: RiskBand;
  /** Highest level usually appropriate for this risk/reversibility combination. */
  maxLevel: number;
  guidance: string;
}

export const AGENCY_RISK_MATRIX: MatrixCell[] = [
  {
    reversibility: 'reversible',
    risk: 'low',
    maxLevel: 7,
    guidance: 'Safe to delegate ownership. Ideal for autonomous, auditable, low-stakes work.',
  },
  {
    reversibility: 'reversible',
    risk: 'moderate',
    maxLevel: 6,
    guidance: 'Decide and inform. The delegate acts, then surfaces what was decided.',
  },
  {
    reversibility: 'reversible',
    risk: 'high',
    maxLevel: 5,
    guidance: 'Act within boundaries. Define an operating envelope and clear escalation triggers.',
  },
  {
    reversibility: 'irreversible',
    risk: 'low',
    maxLevel: 5,
    guidance: 'Act within boundaries, but make irreversibility itself an escalation trigger.',
  },
  {
    reversibility: 'irreversible',
    risk: 'moderate',
    maxLevel: 4,
    guidance: 'Prepare for review. Produce the work; the leader authorizes the irreversible step.',
  },
  {
    reversibility: 'irreversible',
    risk: 'high',
    maxLevel: 3,
    guidance: 'Recommend only. Keep the decision with the leader; the cost of being wrong is high.',
  },
];

export function lookupMatrixCell(reversibility: Reversibility, risk: RiskBand): MatrixCell {
  return (
    AGENCY_RISK_MATRIX.find((c) => c.reversibility === reversibility && c.risk === risk) ??
    AGENCY_RISK_MATRIX[AGENCY_RISK_MATRIX.length - 1]
  );
}

/** Fields captured by the Delegation Brief builder. */
export interface DelegationBrief {
  level: number;
  delegate: Delegate;
  outcome: string;
  authority: string;
  boundaries: string;
  escalation: string;
  reporting: string;
  definitionOfDone: string;
}

export const EMPTY_BRIEF: DelegationBrief = {
  level: 5,
  delegate: 'human',
  outcome: '',
  authority: '',
  boundaries: '',
  escalation: '',
  reporting: '',
  definitionOfDone: '',
};

/**
 * Assemble the leader's delegation sentence from a brief. Empty fields fall
 * back to bracketed placeholders so the structure is always visible, unless
 * `omitEmpty` is set (a saved request reads only what it says). `levelLabel`
 * replaces "Level N — name" for a workspace on the five-level ladder.
 */
export function composeDelegationSentence(
  brief: DelegationBrief,
  opts: { levelLabel?: string; omitEmpty?: boolean } = {},
): string {
  const lvl = DELEGATION_LEVELS.find((l) => l.level === brief.level);
  const name = lvl ? lvl.name : 'Delegate';
  // A value ends its own sentence here, so its own closing period is dropped.
  const ph = (value: string, placeholder: string) => (value.trim() ? value.trim().replace(/\.+$/, '') : `[${placeholder}]`);
  const who = brief.delegate === 'ai' ? 'You (AI) have' : 'You have';
  const line = (value: string, placeholder: string, say: (v: string) => string) =>
    opts.omitEmpty && !value.trim() ? null : say(ph(value, placeholder));
  // A value written as its own sentence ("A facilitator is booked", "Book a facilitator")
  // follows its lead after a colon; a phrase ("pick the vendor", "SSO is live") runs on.
  const lead = (prefix: string, v: string) => (/^[A-Z](?:[a-z]|\s)/.test(v) ? `${prefix}: ${v}.` : `${prefix} ${v}.`);
  return [
    `This is a ${opts.levelLabel ?? `Level ${brief.level} delegation — ${name}`}.`,
    line(brief.outcome, 'outcome', (v) => lead('The desired outcome is', v)),
    line(brief.authority, 'permissions', (v) => lead(`${who} authority to`, v)),
    line(brief.boundaries, 'boundaries', (v) => lead('Do not', v)),
    line(brief.escalation, 'escalation triggers', (v) => lead('Escalate if', v)),
    // A report line that is already an instruction ("Report exceptions …", "Tell me what you set") is the sentence.
    line(brief.reporting, 'feedback expectation', (v) => (/^(report|inform|confirm|return|provide|tell|send|share)\b/i.test(v) ? `${v}.` : lead('Report back', v))),
    line(brief.definitionOfDone, 'definition of done', (v) => lead('Done means', v)),
  ].filter(Boolean).join(' ');
}
